import { CalendarClock, Link2, Pencil, Repeat, Trash2, Users, Video } from "lucide-react";
import { Link } from "react-router";

import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import type { Meeting } from "@/lib/api/types";
import { detectedZone, isKnownZone, zoneName } from "@/lib/calendar";
import { formatRelative, formatTimeRange } from "@/lib/format";
import { useI18n } from "@/lib/i18n";

const STATUS_TONE = {
  scheduled: "neutral",
  live: "success",
  ended: "neutral",
  cancelled: "danger",
} as const;

export type MeetingCardProps = {
  meeting: Meeting;
  isOrganizer: boolean;
  onEdit: () => void;
  onInvite: () => void;
  onCancel: () => void;
  onCopyLink: () => void;
};

export function MeetingCard({
  meeting,
  isOrganizer,
  onEdit,
  onInvite,
  onCancel,
  onCopyLink,
}: MeetingCardProps) {
  const { t, locale } = useI18n();

  const here = detectedZone();
  const elsewhere =
    isKnownZone(meeting.timezone) && meeting.timezone !== here ? zoneName(here, locale) : null;
  const accepted = meeting.invitees.filter((invitee) => invitee.status === "accepted").length;

  return (
    <article className="flex flex-col gap-3 rounded-panel border border-line bg-surface p-4 shadow-panel sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-ink">{meeting.title}</h3>
          {meeting.status === "live" ? <Badge tone="success">{t("meeting.liveNow")}</Badge> : null}
          {meeting.status === "cancelled" ? (
            <Badge tone="danger">{t("meeting.cancelled")}</Badge>
          ) : null}
          {meeting.recurrence.kind !== "none" ? (
            <Badge tone={STATUS_TONE[meeting.status]}>
              <Repeat className="h-3 w-3" /> {t("meeting.repeats")}
            </Badge>
          ) : null}
          {meeting.externalRef !== null ? (
            <Badge tone="brand">
              {t("meeting.fromService", { service: meeting.externalRef.service })}
            </Badge>
          ) : null}
        </div>

        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" />
            {}
            {elsewhere === null
              ? formatTimeRange(meeting.startsAt, meeting.endsAt, locale)
              : t("meeting.inZone", {
                  time: formatTimeRange(meeting.startsAt, meeting.endsAt, locale),
                  zone: elsewhere,
                })}
          </span>
          <span>{formatRelative(meeting.startsAt, locale)}</span>
          {meeting.invitees.length > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {t("meeting.accepted", { accepted, total: meeting.invitees.length })}
            </span>
          ) : null}
        </p>

        {meeting.description !== "" ? (
          <p className="mt-2 line-clamp-2 text-sm text-ink-muted">{meeting.description}</p>
        ) : null}

        <div className="mt-2 flex items-center gap-2">
          <Avatar
            name={meeting.organizer.displayName}
            size="xs"
            url={meeting.organizer.avatarUrl}
          />
          <span className="text-xs text-ink-muted">{meeting.organizer.displayName}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <IconButton label={t("meeting.copyJoinLink")} onClick={onCopyLink}>
          <Link2 className="h-4 w-4" />
        </IconButton>
        {isOrganizer ? (
          <>
            <IconButton label={t("meeting.invitePeople")} onClick={onInvite}>
              <Users className="h-4 w-4" />
            </IconButton>
            <IconButton label={t("common.edit")} onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </IconButton>
            <IconButton label={t("meeting.cancelThis")} onClick={onCancel}>
              <Trash2 className="h-4 w-4" />
            </IconButton>
          </>
        ) : null}
        <Link
          className="inline-flex h-8 items-center gap-2 rounded-xl bg-brand px-3 text-xs font-medium text-brand-ink transition-colors hover:bg-brand-strong"
          to={`/${meeting.roomSlug}`}
        >
          <Video className="h-4 w-4" /> {t("common.join")}
        </Link>
      </div>
    </article>
  );
}

export function MeetingCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-panel border border-line bg-surface p-4 shadow-panel sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <Skeleton className="h-4 w-48 max-w-full" />
        <Skeleton className="mt-2.5 h-3 w-64 max-w-full" />
        <div className="mt-3 flex items-center gap-2">
          <Skeleton className="h-6 w-6 rounded-full" />
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Skeleton className="h-8 w-8 rounded-xl" />
        <Skeleton className="h-8 w-8 rounded-xl" />
        <Skeleton className="h-8 w-20 rounded-xl" />
      </div>
    </div>
  );
}
